import React, { useEffect, useState } from "react";
import InlineCheckbox from "./InlineCheckbox";
import TextInput from "./TextInput";
import RadioButtons from "./RadioButtons";
import DateInput from "./DateInput";
import AddInput from "./AddInput";
import SimpleCombobox from "./SimpleCombobox";
import TextEditor from "./TextEditor";
import api from "../api";

const inputClassName =
    "min-h-[44px] w-full rounded-xl border border-slate-300 bg-white px-3 shadow-sm focus:border-primary focus:ring-primary dark:border-slate-700 dark:bg-slate-950";

const selectClassName =
    "min-h-[44px] w-full rounded-xl border border-slate-300 bg-white shadow-sm focus:border-primary focus:ring-primary dark:border-slate-700 dark:bg-slate-950";

function SectionCard({ title, description, children, className = "" }) {
    return (
        <section
            className={`rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-800 dark:bg-slate-900/40 sm:p-5 ${className}`}
        >
            {(title || description) && (
                <div className="mb-4">
                    {title && (
                        <h3 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-700 dark:text-slate-200">
                            {title}
                        </h3>
                    )}
                    {description && (
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            {description}
                        </p>
                    )}
                </div>
            )}
            {children}
        </section>
    );
}

function FormFooter({
    leftAction,
    rightPrimaryLabel,
    onRightPrimary,
    loading,
    submitError,
    rightPrimaryType = "button",
}) {
    return (
        <div className="border-t border-slate-200 bg-slate-50/90 px-4 py-4 dark:border-slate-800 dark:bg-slate-900/80 sm:px-6">
            <div className="flex flex-col gap-3">
                {submitError && (
                    <p className="text-sm text-red-600 dark:text-red-400">
                        {submitError}
                    </p>
                )}
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {leftAction}
                    <button
                        type={rightPrimaryType}
                        onClick={onRightPrimary}
                        className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        {loading ? (
                            <span className="h-5 w-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                        ) : (
                            rightPrimaryLabel
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

function FamilyHistoryCard({ label, patient, handleChange, fieldPrefix }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {label}
            </p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <InlineCheckbox
                    value={patient?.[`${fieldPrefix}Dm`]}
                    handleChange={handleChange}
                    name={`${fieldPrefix}Dm`}
                    label="Diabetes"
                    containerClassName="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-900"
                />
                <InlineCheckbox
                    value={patient?.[`${fieldPrefix}Hta`]}
                    handleChange={handleChange}
                    name={`${fieldPrefix}Hta`}
                    label="HTA"
                    containerClassName="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-900"
                />
                <InlineCheckbox
                    value={patient?.[`${fieldPrefix}Glaucoma`]}
                    handleChange={handleChange}
                    name={`${fieldPrefix}Glaucoma`}
                    label="Glaucoma"
                    containerClassName="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-900"
                />
            </div>
        </div>
    );
}

function HistoryCheckboxGrid({ items, patient, handleChange }) {
    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((item) => (
                <InlineCheckbox
                    key={item.name}
                    value={patient?.[item.name]}
                    handleChange={handleChange}
                    name={item.name}
                    label={item.label}
                    containerClassName="rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-950"
                />
            ))}
        </div>
    );
}

const MedicalHistory = function (props) {
    const goBack = () => {
        props.setTab("DATOS PERSONALES");
    };

    return (
        <form
            className="overflow-hidden rounded-[28px] border border-slate-200 bg-white text-black shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
            onSubmit={(event) => props.handleSubmit(event)}
        >
            <div className="flex flex-col gap-4 p-4 sm:gap-5 sm:p-6">
                <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.8fr)]">
                    <SectionCard
                        title="Antecedentes personales"
                        description="Marca los antecedentes relevantes y agrega detalles cuando haga falta."
                    >
                        <HistoryCheckboxGrid
                            items={[
                                { name: "dm", label: "DM" },
                                { name: "hta", label: "HTA" },
                                { name: "gl", label: "Glaucoma" },
                            ]}
                            patient={props.patient}
                            handleChange={props.handleChange}
                        />
                        <TextEditor
                            name="othersPersonal"
                            label=""
                            value={props.patient?.othersPersonal}
                            handleChange={props.handleChange}
                            error={null}
                            required={false}
                            containerClassName="mt-4"
                            height={160}
                        />
                    </SectionCard>

                    <SectionCard
                        title="Medicamentos"
                        description="Registra tratamientos o medicaciones activas relevantes."
                    >
                        <TextEditor
                            name="medications"
                            label=""
                            value={props.patient?.medications}
                            handleChange={props.handleChange}
                            error={null}
                            required={false}
                            containerClassName="mt-1"
                            height={220}
                        />
                    </SectionCard>
                </div>

                <div className="grid gap-4 xl:grid-cols-2">
                    <SectionCard title="Antecedentes oculares">
                        <HistoryCheckboxGrid
                            items={[
                                { name: "strabismus", label: "Estrabismo" },
                                { name: "glaucoma", label: "Glaucoma" },
                            ]}
                            patient={props.patient}
                            handleChange={props.handleChange}
                        />
                        <TextEditor
                            name="othersOcular"
                            label=""
                            value={props.patient?.othersOcular}
                            handleChange={props.handleChange}
                            error={null}
                            required={false}
                            containerClassName="mt-4"
                            height={160}
                        />
                    </SectionCard>

                    <SectionCard title="Cirugias oftalmologicas">
                        <HistoryCheckboxGrid
                            items={[
                                { name: "cataractSurgery", label: "Cataratas" },
                                { name: "glaucomaSurgery", label: "Glaucoma" },
                                { name: "refractiveSurgery", label: "Refractiva" },
                            ]}
                            patient={props.patient}
                            handleChange={props.handleChange}
                        />
                        <TextEditor
                            name="othersSurgery"
                            label=""
                            value={props.patient?.othersSurgery}
                            handleChange={props.handleChange}
                            error={null}
                            required={false}
                            containerClassName="mt-4"
                            height={160}
                        />
                    </SectionCard>
                </div>

                <SectionCard
                    title="Antecedentes familiares"
                    description="Organizado por familiar para que en mobile no se comprima como tabla."
                >
                    <div className="grid gap-4 lg:grid-cols-2">
                        <FamilyHistoryCard
                            label="Padre"
                            patient={props.patient}
                            handleChange={props.handleChange}
                            fieldPrefix="father"
                        />
                        <FamilyHistoryCard
                            label="Madre"
                            patient={props.patient}
                            handleChange={props.handleChange}
                            fieldPrefix="mother"
                        />
                        <FamilyHistoryCard
                            label="Hermanos"
                            patient={props.patient}
                            handleChange={props.handleChange}
                            fieldPrefix="siblings"
                        />
                        <FamilyHistoryCard
                            label="Otros"
                            patient={props.patient}
                            handleChange={props.handleChange}
                            fieldPrefix="other"
                        />
                    </div>
                    <TextEditor
                        name="othersFamily"
                        label=""
                        value={props.patient?.othersFamily}
                        handleChange={props.handleChange}
                        error={null}
                        required={false}
                        containerClassName="mt-4"
                        height={160}
                    />
                </SectionCard>
            </div>

            <FormFooter
                leftAction={
                    <button
                        type="button"
                        className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-primary/30 hover:text-primary dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                        onClick={goBack}
                    >
                        Anterior
                    </button>
                }
                rightPrimaryLabel="Finalizar y guardar"
                rightPrimaryType="submit"
                loading={props.loadingSubmit}
                submitError={props.submitError}
            />
        </form>
    );
};

export const PersonalData = function (props) {
    const today = new Date();
    const minBirthdate = new Date(today.setFullYear(today.getFullYear() - 120));
    const [rucAutofill, setRucAutofill] = useState(true);
    const [legalNameAutofill, setLegalNameAutofill] = useState(true);
    const patient = props.patient;

    const goForward = (event) => {
        event.preventDefault();
        let error = false;
        if (!patient?.firstName) {
            props.handleError("firstName", "Este campo es obligatorio");
            error = true;
        } else {
            props.handleError("firstName", "");
        }
        if (!patient?.lastName) {
            props.handleError("lastName", "Este campo es obligatorio");
            error = true;
        } else {
            props.handleError("lastName", "");
        }
        if (!patient?.sex) {
            props.handleError("sex", "Este campo es obligatorio");
            error = true;
        } else {
            props.handleError("sex", "");
        }
        if (!patient?.city) {
            props.handleError("city", "Este campo es obligatorio");
            error = true;
        } else {
            props.handleError("city", "");
        }
        if (!patient?.phoneNumbers || patient?.phoneNumbers.length === 0) {
            props.handleError("phoneNumbers", "Este campo es obligatorio");
            error = true;
        } else {
            props.handleError("phoneNumbers", "");
        }
        if (!error) {
            props.setTab("ANTECEDENTES");
        }
    };

    useEffect(() => {
        const nextLegalName = `${patient?.firstName ? patient?.firstName : ""} ${
            patient?.lastName ? patient?.lastName : ""
        }`.trim();

        if (legalNameAutofill) {
            const currentLegalName = patient?.legalName || "";
            if (currentLegalName !== nextLegalName) {
                props.handleChange("legalName", nextLegalName);
            }
        }
    }, [
        legalNameAutofill,
        patient?.firstName,
        patient?.lastName,
        patient?.legalName,
        props,
    ]);

    useEffect(() => {
        if (rucAutofill) {
            const nextRuc = patient?.govId || "";
            const currentRuc = patient?.ruc || "";
            if (currentRuc !== nextRuc) {
                props.handleChange("ruc", nextRuc);
            }
        }
    }, [patient?.govId, patient?.ruc, props, rucAutofill]);

    const handleRuc = (field, value) => {
        setRucAutofill(false);
        props.handleChange(field, value);
    };

    const handleLegalName = (field, value) => {
        setLegalNameAutofill(false);
        props.handleChange(field, value);
    };

    const createInsurance = async (newItem) => {
        const response = await api.insurances.create(newItem);
        props.setInsurances((prev) => [...prev, response]);
        props.handleChange("insurance", response);
        return response;
    };

    return (
        <form
            className="overflow-hidden rounded-[28px] border border-slate-200 bg-white text-black shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-white"
            onSubmit={(event) => goForward(event)}
        >
            <div className="flex flex-col gap-4 p-4 sm:gap-5 sm:p-6">
                <SectionCard
                    title="Identidad"
                >
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        <TextInput
                            showErrorMessage={true}
                            required={true}
                            errorMessage={props.errors?.firstName}
                            value={props.patient?.firstName}
                            handleChange={props.handleChange}
                            name="firstName"
                            label="Nombre"
                            className={inputClassName}
                        />
                        <TextInput
                            showErrorMessage={true}
                            required={true}
                            errorMessage={props.errors?.lastName}
                            value={props.patient?.lastName}
                            handleChange={props.handleChange}
                            name="lastName"
                            label="Apellido"
                            className={inputClassName}
                        />
                        <TextInput
                            showErrorMessage={true}
                            errorMessage={props.errors?.legalName}
                            value={props.patient?.legalName}
                            handleChange={handleLegalName}
                            name="legalName"
                            label="Razon social"
                            className={inputClassName}
                        />
                        <div className="md:col-span-2 xl:col-span-1">
                            <RadioButtons
                                required={true}
                                errorMessage={props.errors?.sex}
                                defaultValue={props.patient?.sex}
                                value={props.patient?.sex}
                                handleChange={props.handleChange}
                                name="sex"
                                label="Sexo"
                                options={props.radio}
                                optionsContainerStyle="grid grid-cols-1 gap-3 sm:grid-cols-2"
                            />
                        </div>
                        <DateInput
                            required={false}
                            mindate={minBirthdate.toISOString().substring(0, 10)}
                            maxdate={new Date().toISOString().substring(0, 10)}
                            errorMessage={props.errors?.birthdate}
                            defaultValue={
                                props.patient?.birthdate
                                    ? props.patient?.birthdate
                                    : null
                            }
                            handleChange={props.handleChange}
                            name="birthdate"
                            label="Fecha de nacimiento"
                            className={inputClassName}
                        />
                        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                Edad
                            </label>
                            <p className="mt-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                                {props.patient?.age || "-"}
                            </p>
                        </div>
                        <TextInput
                            showErrorMessage={true}
                            required={false}
                            errorMessage={props.errors?.govId}
                            value={props.patient?.govId}
                            handleChange={props.handleChange}
                            name="govId"
                            label="Cedula de identidad"
                            className={inputClassName}
                        />
                        <TextInput
                            showErrorMessage={true}
                            errorMessage={props.errors?.ruc}
                            value={props.patient?.ruc}
                            handleChange={handleRuc}
                            name="ruc"
                            label="RUC"
                            className={inputClassName}
                        />
                        <div className="md:col-span-2 xl:col-span-1">
                            <SimpleCombobox
                                inputClassName="relative mt-1"
                                options={props.insurances}
                                required={false}
                                error={props.errors?.insurance}
                                value={props.patient?.insurance}
                                handleChange={props.handleChange}
                                create={createInsurance}
                                name="insurance"
                                label="Seguro"
                                className={selectClassName}
                            />
                        </div>
                    </div>
                </SectionCard>

                <SectionCard
                    title="Contacto y ubicacion"
                >
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <AddInput
                            onlyNumbers={true}
                            handleDelete={props.removePhone}
                            required={true}
                            placeholder="0980 000 000"
                            errorMessage={props.errors?.phoneNumbers}
                            unit="Numero"
                            value={props.patient?.phoneNumbers}
                            handleChange={props.handlePhones}
                            name="phoneNumbers"
                            label="Celular / Telefono"
                            className="min-h-[44px] w-full rounded-xl border-0 bg-transparent px-3 focus:ring-0 focus-visible:outline-none"
                            containerClassName="lg:col-span-2"
                        />
                        <TextInput
                            showErrorMessage={true}
                            errorMessage={props.errors?.email}
                            value={props.patient?.email}
                            handleChange={props.handleChange}
                            name="email"
                            label="Correo electronico"
                            className={inputClassName}
                        />
                        <div>
                            <SimpleCombobox
                                inputClassName="relative mt-1"
                                required={true}
                                error={props.errors?.city}
                                value={props.patient?.city}
                                handleChange={props.handleChange}
                                name="city"
                                label="Ciudad"
                                className={selectClassName}
                                options={props?.cities}
                            />
                        </div>
                    </div>
                </SectionCard>
            </div>

            <FormFooter
                leftAction={<div className="hidden sm:block" />}
                rightPrimaryLabel="Siguiente"
                rightPrimaryType="submit"
                loading={false}
                submitError={null}
            />
        </form>
    );
};

export default function FormNewPatient(props) {
    const patient = props.patient;

    const getAge = (dateString) => {
        var today = new Date();
        var birthDate = new Date(dateString);
        var age = today.getFullYear() - birthDate.getFullYear();
        var m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    useEffect(() => {
        const nextAge =
            patient?.birthdate === new Date().toISOString().substring(0, 10) ||
            !patient?.birthdate
                ? "-"
                : getAge(patient?.birthdate);

        if (patient?.age !== nextAge) {
            props.handleChange("age", nextAge);
        }
    }, [patient?.age, patient?.birthdate, props]);

    return (
        <div className={props.onModal ? "w-full" : ""}>
            {props.tab === "DATOS PERSONALES" && (
                <PersonalData
                    patient={props.patient}
                    handleChange={props.handleChange}
                    handleError={props.handleError}
                    errors={props.error}
                    radio={props.radio}
                    cities={props.cities}
                    insurances={props.insurances}
                    options={props.options}
                    handlePhones={props.handlePhones}
                    removePhone={props.removePhone}
                    setTab={props.setTab}
                    onModal={props.onModal}
                    setInsurances={props.setInsurances}
                />
            )}
            {props.tab === "ANTECEDENTES" && (
                <MedicalHistory
                    patient={props.patient}
                    errors={props.error}
                    setTab={props.setTab}
                    handleError={props.handleError}
                    handleChange={props.handleChange}
                    loadingSubmit={props.loadingSubmit}
                    handleSubmit={props.handleSubmit}
                    submitError={props.submitError}
                    onModal={props.onModal}
                />
            )}
        </div>
    );
}